import { Component, HostBinding, HostListener, inject, input, OnInit, output, TemplateRef } from '@angular/core';
import { SbReactionComponent } from './sb-reaction/sb-reaction.component';
import { DisplayMessage } from '../sb-model/display-message';
import { SbEditMessageComponent } from './sb-edit-message/sb-edit-message.component';
import { EmojiComponent, EmojiData, EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { DialogData } from '../../../common/components/dialog/dialog.component';
import { DialogService } from '../../../services/dialog/dialog.service';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { NgClass } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { Room } from '../../../../shared-api/room';
import { ReactionsService } from '../../../services/chat-message/reactions/reactions.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { PinnedMessageService } from '../../../services/pinMessages/pinned-message.service';
import { MatrixService } from '../../../services/matrix/matrix.service';
import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle
} from '@angular/material/dialog';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MessageService } from '../../../services/message/message.service';
import { filter, finalize, from, switchMap } from 'rxjs';
import { MessageToolbarTestIds } from './message-toolbar.test-ids';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { SbRespondMessageComponent } from './sb-respond-message/sb-respond-message.component';

const DELETE_MESSAGE_TIME_WINDOW_IN_MS = 24 * 60 * 60 * 1000;

const MESSAGE_TOOLBAR_TOOLTIP_DEFAULT_OPTIONS: MatTooltipDefaultOptions = {
  disableTooltipInteractivity: true
} as MatTooltipDefaultOptions;

@Component({
  selector: 'app-message-toolbar',
  imports: [
    SbReactionComponent,
    SbEditMessageComponent,
    EmojiComponent,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatToolbar,
    NgClass,
    MatDivider,
    MatTooltip,
    ButtonComponent,
    FormsModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatInput,
    MatFormField,
    CdkTextareaAutosize,
    ReactiveFormsModule,
    SbRespondMessageComponent
  ],
  templateUrl: './message-toolbar.component.html',
  styleUrl: './message-toolbar.component.scss',
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: MESSAGE_TOOLBAR_TOOLTIP_DEFAULT_OPTIONS }]
})

/**
 * Diese Komponente enthält die Funktionen und Anzeige der Toolbar für eine Nachricht.
 * Keine Afo erfüllt, Begründung:
 * Hier werden nur optische Funktionalitäten gesetzt.
 */
export class MessageToolbarComponent implements OnInit {
  protected readonly testIds = MessageToolbarTestIds;
  room = input<Room>();
  message = input<DisplayMessage>();
  toolbarClosed = output<void>();
  menuComponents = [
    { id: 1, title: 'respond' },
    { id: 2, title: 'delete' },
    { id: 3, title: 'report' },
    { id: 4, title: 'pin' }
  ];
  @HostBinding('class.menu-open') isMenuOpen = false;
  toolbarOpen = true;
  protected emojiList: (EmojiData | null)[] = [];
  protected toolbarMessageId = output<string>();
  protected readonly clientService = inject(MatrixService);
  protected readonly dialogService = inject(DialogService);
  protected readonly messageService = inject(MessageService);
  protected readonly reactionService = inject(ReactionsService);
  protected readonly snackbarService = inject(SnackbarService);
  protected readonly pinnedMessageService = inject(PinnedMessageService);
  protected readonly matDialog = inject(MatDialog);
  protected readonly fb = inject(FormBuilder);
  protected reportForm: FormGroup = this.fb.group({
    reportReason: ['', Validators.required]
  });
  protected readonly input = input;
  private readonly emojiService = inject(EmojiService);

  setToolbarMessageId(output: string | undefined): void {
    this.toolbarMessageId.emit(output ?? '');
  }

  getRecentEmojis(): { emojiName: string; count: number }[] {
    const storedEmojis = localStorage.getItem('emoji-mart.frequently') ?? `{"thumbsup":3,"thumbsdown":2,"grinning":1}`;
    const emojiObject = JSON.parse(storedEmojis);
    return Object.entries(emojiObject).map(([emojiName, count]) => ({
      emojiName,
      count: Number(count)
    }));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Toolbar offen lassen bei Klicks innerhalb der Toolbar selbst oder im CDK-Overlay (mat-menu).
    const keepOpen = !!target.closest('.message-toolbar') || !!target.closest('.cdk-overlay-container');
    this.isMenuOpen = keepOpen;
    this.toolbarOpen = keepOpen;
  }

  ngOnInit(): void {
    this.setUnicodeList();
  }

  openDeleteDialog(message: DisplayMessage): void {
    // roomId und eventId vor dem Öffnen des Dialogs erfassen, da die Komponente
    // während des Dialogs durch ein @for-Re-Rendering zerstört werden kann
    // (z.B. wenn ein Matrix-Sync die temporäre Event-ID durch die endgültige ersetzt).
    const roomId = this.room()?.roomId;
    // Original-Event adressieren (nicht das einzelne m.replace-Edit), damit der
    // with_rel_types-Cascade die Nachricht vollständig löscht (TIME-4164).
    const eventId = message?.originalEventId;

    // Default DialogData structure
    const dialogData: DialogData = {
      title: 'LÖSCHEN DER NACHRICHT',
      message: message.formattedBody,
      subMessage: 'Wollen Sie diese Nachricht unwiderruflich und für alle Chatteilnehmer löschen?',
      confirmText: 'Nachricht löschen',
      cancelText: 'Abbrechen',
      cancelDataTestId: MessageToolbarTestIds.buttonCancelDelete,
      confirmDataTestId: MessageToolbarTestIds.buttonConfirmDelete,
      dialogContentTestId: MessageToolbarTestIds.dialogDeleteContent,
      dialogActionTestId: MessageToolbarTestIds.dialogDeleteActions,
      svgIcon: 'circle-error',
      isLongMessage: true
    };

    // Wichtig für TIME-669: Die Toolbar stößt nur die Redaction an. Das eigentliche Umschalten
    // auf "gelöscht" erfolgt erst über den Store nach server-bestätigter Redaction.
    // Erfüllt die Anforderung A_25575-01, A_25576-01, A_25577-01.
    this.dialogService
      .openErrorDialog(dialogData, '28.6rem', '21.8rem')
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => from(this.redactMessage(roomId, eventId))),
        finalize(() => this.closeToolbar())
      )
      .subscribe();
  }

  closeToolbar(): void {
    this.toolbarClosed.emit();
  }

  protected onDeleteMenuItemClick(event: Event, message: DisplayMessage): void {
    if (!this.canDeleteMessage(message)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.openDeleteDialog(message);
  }

  canDeleteMessage(message: DisplayMessage | undefined): boolean {
    return this.isOwnMessage(message) && !message?.isRedacted && this.isMessageWithinDeleteTimeWindow(message);
  }

  protected showDeleteMessageMenuItem(message: DisplayMessage | undefined): boolean {
    return this.isOwnMessage(message) && !message?.isRedacted;
  }

  async reportEvent(eventId: string): Promise<void> {
    const roomId = this.room()?.roomId;
    if (!roomId) {
      throw new Error(`Could not find roomId when calling reportEvent`);
    }
    try {
      await this.clientService.client.reportEvent(roomId, eventId, -50, this.reportForm.controls['reportReason'].value);
      this.snackbarService.success('Ihre Meldung wurde erfolgreich übermittelt. Vielen Dank für Ihren Hinweis.');
      this.matDialog.closeAll();
    } catch {
      this.snackbarService.error(
        'Fehler: Ihre Meldung konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.'
      );
    }
  }

  openReportDialog(reportDialog: TemplateRef<MatDialog>, message: DisplayMessage): void {
    this.matDialog.open(reportDialog, {
      data: {
        eventId: message.eventId,
        messageText: message.formattedBody,
        sender: message.sender
      },
      height: 'auto',
      width: 'auto',
      panelClass: ['warning-dialog-container', 'report-dialog']
    });
  }

  protected async sendReaction(emoji: EmojiData): Promise<void> {
    const message = this.message();
    if (message && this.room()) {
      try {
        await this.reactionService.sendReaction(message.reactionsArray, message.eventId, emoji, this.room());
        // eslint-disable-next-line
      } catch (e: any) {
        console.warn(e);
        this.snackbarService.error('Fehler beim Senden der Reaktion');
      }
    }
    this.closeToolbar();
  }

  private setUnicodeList(): void {
    const topThreeEmojis = this.getRecentEmojis()
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    this.emojiList = topThreeEmojis.map(emoji => this.emojiService.getData(emoji.emojiName));
  }

  /**
   * Der Benutzer kann seine eigene Nachricht löschen und es wird allen im Raum als gelöscht angezeigt.
   * Die Nachricht wird als gelöscht ersetzt und es wird das Datum und die Uhrzeit angezeigt, zu der sie gelöscht wurde und von wem.
   * TIME-669: Diese Methode triggert ausschließlich die Redaction-Anfrage; die sichtbare Löschung
   * erfolgt erst, wenn der ChatStore die server-bestätigte Redaction verarbeitet.
   */
  private async redactMessage(roomId: string | undefined, eventId: string | undefined): Promise<void> {
    try {
      await this.messageService.deleteMessage(roomId, eventId);
      // eslint-disable-next-line
    } catch (e: any) {
      console.error(`Deletion of message (${eventId}) failed`, e);
      this.snackbarService.error('Fehler beim Löschen der Nachricht');
    }
  }

  private isOwnMessage(message: DisplayMessage | undefined): boolean {
    const senderId =
      (message as unknown as { senderId?: string; sender?: string } | undefined)?.senderId ?? message?.sender;
    return this.clientService.client.getUserId() === senderId;
  }

  private isMessageWithinDeleteTimeWindow(message: DisplayMessage | undefined): boolean {
    const messageTimestamp = this.getMessageTimestamp(message);

    return messageTimestamp === undefined || Date.now() - messageTimestamp <= DELETE_MESSAGE_TIME_WINDOW_IN_MS;
  }

  private getMessageTimestamp(message: DisplayMessage | undefined): number | undefined {
    const candidateMessage = message as unknown as
      | {
          originServerTs?: unknown;
          origin_server_ts?: unknown;
          timestamp?: unknown;
          createdAt?: unknown;
          created_at?: unknown;
          ts?: unknown;
          event?: { getTs?: () => number; event?: { origin_server_ts?: number } };
          first?: { getTs?: () => number; event?: { origin_server_ts?: number } };
          curr?: { getTs?: () => number; event?: { origin_server_ts?: number } };
        }
      | undefined;

    const timestampCandidates = [
      candidateMessage?.originServerTs,
      candidateMessage?.origin_server_ts,
      candidateMessage?.timestamp,
      candidateMessage?.createdAt,
      candidateMessage?.created_at,
      candidateMessage?.ts,
      candidateMessage?.event?.getTs?.(),
      candidateMessage?.event?.event?.origin_server_ts,
      candidateMessage?.first?.getTs?.(),
      candidateMessage?.first?.event?.origin_server_ts,
      candidateMessage?.curr?.getTs?.(),
      candidateMessage?.curr?.event?.origin_server_ts
    ];

    for (const candidate of timestampCandidates) {
      const timestamp = this.toTimestamp(candidate);
      if (timestamp !== undefined) {
        return timestamp;
      }
    }

    return undefined;
  }

  private toTimestamp(value: unknown): number | undefined {
    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value < 100000000000 ? value * 1000 : value;
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        return this.toTimestamp(numericValue);
      }

      const parsedDate = Date.parse(value);
      if (!Number.isNaN(parsedDate)) {
        return parsedDate;
      }
    }

    return undefined;
  }
}
